<template>
  <UModal
    v-if="!isMobile && prompt && module"
    :open="!!prompt"
    title="خرید ماژول"
    @update:open="onOpenChange"
  >
    <template #body>
      <PromptContent
        v-if="prompt && module"
        :title="moduleTitle"
        :days-left="daysLeft"
        :remaining-ms="remainingMs"
        :annual-price="module?.annualPrice ?? 0"
        :busy="busy"
        @purchase="purchase"
      />
    </template>
  </UModal>

  <Teleport v-else to="body">
    <div
      v-if="prompt && module"
      class="fixed inset-0 z-50 flex items-end bg-black/45 sm:hidden"
      @click.self="closeModulePurchase"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="module-prompt-title"
        class="w-full rounded-t-3xl bg-[var(--ui-bg)] px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6 shadow-2xl"
      >
        <div class="mx-auto mb-5 h-1 w-10 rounded-full bg-[var(--ui-border)]" />
        <PromptContent
          :title="moduleTitle"
          :days-left="daysLeft"
          :remaining-ms="remainingMs"
          :annual-price="module?.annualPrice ?? 0"
          :busy="busy"
          @purchase="purchase"
        />
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { formatGroupedFa } from '~/utils/money'
import { apiErrorMessage } from '~/utils/apiError'
import { SUBSCRIPTION_TERM_MS } from '~/utils/subscriptionPricing'
import { getPlanCatalogModuleTitle, isPlanCatalogModule } from '~/utils/modules/planCatalog'
import { defineComponent, h, resolveComponent } from 'vue'

const PromptContent = defineComponent({
  props: {
    title: { type: String, required: true },
    daysLeft: { type: Number, required: true },
    remainingMs: { type: Number, required: true },
    annualPrice: { type: Number, required: true },
    busy: { type: Boolean, required: true },
  },
  emits: ['purchase'],
  setup(props, { emit }) {
    const amount = computed(() => Math.ceil(props.annualPrice * props.remainingMs / SUBSCRIPTION_TERM_MS))
    return () => h('div', { class: 'space-y-4' }, [
      h('div', [
        h('h2', { id: 'module-prompt-title', class: 'text-lg font-bold' }, `برای استفاده از ${props.title}، این ماژول را خریداری کنید`),
        h('p', { class: 'mt-2 text-sm text-[var(--ui-text-muted)]' }, `پس از تأیید پرداخت، این امکان تا پایان اشتراک فعلی فعال می‌شود.`),
      ]),
      h('div', { class: 'flex justify-between rounded-xl bg-[var(--ui-bg-elevated)] p-3 text-sm' }, [
        h('span', `هزینه برای ${props.daysLeft} روز باقی‌مانده`),
        h('strong', { class: 'tabular-nums' }, `${formatGroupedFa(amount.value)} تومان`),
      ]),
      h(resolveComponent('UButton'), {
        color: 'primary',
        block: true,
        loading: props.busy,
        disabled: amount.value <= 0,
        onClick: () => emit('purchase'),
      }, () => 'خرید و پرداخت'),
    ])
  },
})

const { prompt, closeModulePurchase } = useModulePurchasePrompt()
const auth = useAuthStore()
const toast = useToast()
const isMobile = ref(false)
const busy = ref(false)
const { data } = await useFetch<{ modules: Array<{ key: string; name?: { fa?: string; en?: string }; annualPrice: number }> }>('/api/plans')
const module = computed(() => data.value?.modules.find(item =>
  item.key === prompt.value?.key && isPlanCatalogModule(item),
))
const moduleTitle = computed(() => module.value ? getPlanCatalogModuleTitle(module.value) : prompt.value?.title || '')
const remainingMs = computed(() => Math.max(
  0,
  (auth.subscription.expiresAt ? new Date(auth.subscription.expiresAt).getTime() : Date.now()) - Date.now(),
))
const daysLeft = computed(() => Math.ceil(remainingMs.value / (24 * 60 * 60 * 1000)))

onMounted(() => {
  const query = window.matchMedia('(max-width: 639px)')
  isMobile.value = query.matches
  query.addEventListener('change', event => { isMobile.value = event.matches })
})

function onOpenChange(open: boolean) {
  if (!open) closeModulePurchase()
}

async function purchase() {
  if (!prompt.value || busy.value) return
  busy.value = true
  try {
    const gateways = await $fetch<{ items: Array<{ provider: string }> }>('/api/billing/gateways')
    const response = await $fetch<{ redirectUrl: string }>('/api/billing/modules/checkout', {
      method: 'POST',
      body: { moduleKey: prompt.value.key, provider: gateways.items[0]?.provider },
    })
    closeModulePurchase()
    await navigateTo(response.redirectUrl, { external: true })
  } catch (error: unknown) {
    toast.add({ title: 'خطا', description: apiErrorMessage(error, 'پرداخت ماژول انجام نشد'), color: 'error' })
  } finally {
    busy.value = false
  }
}
</script>
